import { IntlShape } from "react-intl";

import { platformTranslationKeys } from "@app/constants/platform";

/**
 * Resolves a platform's display name via the shared `platformTranslationKeys`. Falls back to the
 * given name (e.g. the aggregate's DB name) or `#id` when the platform isn't in the key map.
 */
export const getPlatformName = (intl: IntlShape, platformId: number, fallback?: string): string => {
  const key = platformTranslationKeys.find((platform) => platform.id === platformId);
  if (key) {
    return intl.formatMessage({ id: key.name });
  }
  return fallback ?? `#${platformId}`;
};

/**
 * Orders platforms for a dashboard: enabled platforms first, then within each group the curated
 * `platformTranslationKeys` sequence. `isEnabled` is a selector because each dashboard has its own
 * enabled flag.
 *
 * Platforms outside that list are dropped on purpose. The API hands back every row in `Platforms`,
 * including the ones that are not comparison sites (the gaps at ids 5–8), and those have no
 * business in a country sidebar. The price is that a genuinely new country stays invisible until
 * `platformTranslationKeys` is extended — which is the same step that gives it a name, so it is
 * hard to forget once someone opens the dashboard.
 */
export const orderPlatformsByKeys = <T extends { platformId: number }>(
  platforms: T[],
  isEnabled: (platform: T) => boolean
): T[] =>
  platformTranslationKeys
    .flatMap((key) => {
      const match = platforms.find((platform) => platform.platformId === key.id);
      return match ? [match] : [];
    })
    // Stable sort keeps the key order within the enabled and disabled groups.
    .sort((a, b) => Number(isEnabled(b)) - Number(isEnabled(a)));
